// Import react libs
import { useState, useEffect, useRef, useCallback } from 'react';

// Import style
import {
    SlideShowContainer,
    SlideShowImagesContainer,
    SlideShowImage,
    SlideShowOfferBanner,
    SlideShowOffer,
    SlideShowButton,
    SlideShowButtonIcon,
} from './style';

// Import assets
import imageNotFound from '../../assets/images/imageNotFound.png';

import { ReactComponent as leftArrow } from '../../assets/icons/arrowBack.svg';
import { ReactComponent as rightArrow } from '../../assets/icons/arrowForward.svg';

// Import texts
import productTexts from '../../texts/product';

// Define a standart timeout duration
const STANDART_TIMEOUT_DURATION = 3000;
const RAISED_TIMEOUT_DURATION = 10000;

function ProductSlideShow({
    ICON_WIDTH = 30,
    productName = '',
    images = null,
    offer = false,
}) {
    // Store in two state the actual image index and the duration of the timeout
    const [imageIndex, setImageIndex] = useState(0);
    const [timeoutDuration, setTimeoutDuration] = useState(
        STANDART_TIMEOUT_DURATION,
    );

    // Create a ref for the timer allowing to reset time duration after a click on a button
    const resetTimeoutTimerRef = useRef(null);

    // Function to go the next image
    const nextImage = useCallback(() => {
        if (images && images.length > 1) {
            setImageIndex(
                (currentImageIndex) => (currentImageIndex + 1) % images.length,
            );
        }
    }, [images]);

    // Function to go to the previous image
    function previousImage() {
        if (images && images.length > 1) {
            setImageIndex(
                (currentImageIndex) =>
                    (currentImageIndex - 1 + images.length) % images.length,
            );
        }
    }

    // Setup a timeout everytime the image change
    useEffect(() => {
        if (images && images.length > 1) {
            const timer = setTimeout(nextImage, timeoutDuration);

            return () => clearTimeout(timer);
        }
    }, [images, imageIndex, nextImage, timeoutDuration]);

    // Raise the next timeout duration after the click on a button
    function raiseNextTimeoutDuration({
        nextTimeoutDuration = RAISED_TIMEOUT_DURATION,
    }) {
        if (images && images.length > 1) {
            // Change the timeout duration for the raised timeout duration
            setTimeoutDuration(nextTimeoutDuration);

            // If the user had clicked on a button less than 10 sec ago
            // clear the reset timer created at this moment
            if (resetTimeoutTimerRef.current) {
                clearTimeout(resetTimeoutTimerRef.current);
            }

            // Set a timer to reset the timeout to its standart value
            resetTimeoutTimerRef.current = setTimeout(
                () => setTimeoutDuration(STANDART_TIMEOUT_DURATION),
                nextTimeoutDuration,
            );
        }
    }

    return (
        <SlideShowContainer>
            <SlideShowImagesContainer>
                {offer && (
                    <SlideShowOfferBanner>
                        <SlideShowOffer>{productTexts.offer}</SlideShowOffer>
                    </SlideShowOfferBanner>
                )}
                {images && images.length > 0 && images[imageIndex] ? (
                    <SlideShowImage
                        src={images[imageIndex]}
                        alt={productName}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = imageNotFound;
                        }}
                    />
                ) : (
                    <SlideShowImage src={imageNotFound} alt={productName} />
                )}
            </SlideShowImagesContainer>
            {images && images.length > 1 && (
                <>
                    <SlideShowButton
                        type="button"
                        left="true"
                        onClick={() => {
                            previousImage();
                            raiseNextTimeoutDuration(RAISED_TIMEOUT_DURATION);
                        }}
                    >
                        <SlideShowButtonIcon
                            as={leftArrow}
                            width={ICON_WIDTH * 1.5}
                        />
                    </SlideShowButton>
                    <SlideShowButton
                        type="button"
                        right="true"
                        onClick={() => {
                            nextImage();
                            raiseNextTimeoutDuration(RAISED_TIMEOUT_DURATION);
                        }}
                    >
                        <SlideShowButtonIcon
                            as={rightArrow}
                            width={ICON_WIDTH * 1.5}
                        />
                    </SlideShowButton>
                </>
            )}
        </SlideShowContainer>
    );
}

export default ProductSlideShow;
